import { useEffect, useId, useState, type FormEvent } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { useUpdateUser } from "../../../api/queries";
import type { User, UserRole } from "../../../api/types";
import { Button } from "../../../components/Button";
import { Modal } from "../../../components/Modal";
import { useToast } from "../../../toast/ToastContext";
import "../../../components/Form.css";

interface EditUserModalProps {
  /** The user being edited, or null when the dialog is closed — driving
   * this off the target rather than a separate boolean keeps "which user"
   * and "is it open" from ever disagreeing (mirrors RotateSecretModal). */
  user: User | null;
  onClose: () => void;
}

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: "admin", label: "Admin" },
  { value: "operator", label: "Operator" },
  { value: "viewer", label: "Viewer" },
  { value: "billing_admin", label: "Billing admin" },
];

export function EditUserModal({ user, onClose }: EditUserModalProps) {
  const displayNameId = useId();
  const roleId = useId();
  const activeId = useId();
  const { session } = useAuth();
  const { pushToast } = useToast();
  const mutation = useUpdateUser();
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<UserRole>("viewer");
  const [active, setActive] = useState(true);

  // Re-seed the form fields whenever a different user is opened for editing.
  useEffect(() => {
    if (!user) return;
    setDisplayName(user.displayName);
    setRole(user.role);
    setActive(user.active);
  }, [user]);

  const isSelf = user !== null && session?.userId === user.id;

  function handleClose() {
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) return;
    const { id, username } = user;
    mutation.mutate(
      { id, input: { displayName: displayName.trim(), role, active } },
      {
        onSuccess: () => {
          pushToast({ title: `Updated "${username}".`, tone: "success" });
          handleClose();
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't update "${username}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  return (
    <Modal open={user !== null} title={user ? `Edit "${user.username}"` : "Edit user"} onClose={handleClose} initialFocusId={displayNameId}>
      <form onSubmit={handleSubmit} noValidate>
        <label className="form-field" htmlFor={displayNameId}>
          <span>Display name</span>
          <input
            id={displayNameId}
            type="text"
            autoComplete="off"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            maxLength={128}
          />
        </label>

        <label className="form-field" htmlFor={roleId}>
          <span>Role</span>
          <select id={roleId} value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <label className="form-field form-field--checkbox" htmlFor={activeId}>
          <span className="form-field--checkbox__row">
            <input
              id={activeId}
              type="checkbox"
              checked={active}
              disabled={isSelf}
              onChange={(e) => setActive(e.target.checked)}
            />
            <span>Active</span>
          </span>
          <span className="form-field__hint">
            {isSelf
              ? "You can't deactivate your own account."
              : "An inactive user can't sign in. The account and its history stay in place."}
          </span>
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending || !displayName.trim()}>
            {mutation.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
